/**
 * ReassessTrendPanel — when a completed run has a prior completed assessment for
 * the same client + sub-brand, shows the maturity trend per dimension (▲/▼/flat
 * with the delta) so re-assessments evidence movement rather than assume it.
 */
import React, { useCallback, useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp } from "lucide-react";
import api from "../../lib/api";

const DIR = {
  up: { Icon: TrendingUp, cls: "text-emerald-600" },
  down: { Icon: TrendingDown, cls: "text-red-600" },
  flat: { Icon: Minus, cls: "text-zinc-400" },
  new: { Icon: Minus, cls: "text-zinc-300" },
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso || "—"; }
};

export default function ReassessTrendPanel({ runId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/trend`);
      setData(data);
    } catch { setData({ has_prior: false }); }
    finally { setLoaded(true); }
  }, [runId]);

  useEffect(() => { if (open && !loaded) load(); }, [open, loaded, load]);

  const deltaLabel = (d) => (d == null ? "—" : `${d > 0 ? "+" : ""}${d.toFixed(2)}`);

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="reassess-trend-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2" data-testid="trend-toggle">
        <span className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]"><TrendingUp size={14}/> Re-assessment trend</span>
        {open ? <ChevronUp size={16} className="text-zinc-400"/> : <ChevronDown size={16} className="text-zinc-400"/>}
      </button>

      {open && (
        <div className="mt-4" data-testid="trend-body">
          {!loaded && <div className="text-[12px] text-zinc-500">Loading…</div>}
          {loaded && data && !data.has_prior && (
            <div className="text-[12px] text-zinc-500" data-testid="trend-none">No prior completed assessment found for this client &amp; sub-brand. The trend appears on the next re-assessment.</div>
          )}
          {loaded && data?.has_prior && (
            <>
              <div className="flex items-baseline justify-between bg-[#FAF7FF] border border-zinc-200 p-3" data-testid="trend-overall">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.12em] text-zinc-500">Overall vs last</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">Prior assessed {fmtDate(data.prior_completed_at)}</div>
                </div>
                <div className="text-right">
                  <span className="text-xl font-semibold text-[#7737F6] tabular-nums">{data.overall_current ?? "—"}</span>
                  <span className="text-[12px] text-zinc-500"> from {data.overall_previous ?? "—"}</span>
                  <div className={`text-[12px] font-medium ${data.overall_delta > 0 ? "text-emerald-600" : data.overall_delta < 0 ? "text-red-600" : "text-zinc-400"}`}>
                    {deltaLabel(data.overall_delta)}
                  </div>
                </div>
              </div>

              <div className="mt-3 space-y-1.5" data-testid="trend-dimensions">
                {(data.dimensions || []).map((d) => {
                  const cfg = DIR[d.direction] || DIR.flat;
                  const Icon = cfg.Icon;
                  return (
                    <div key={d.dimension_id} className="flex items-center justify-between gap-2 border-b border-zinc-100 pb-1.5" data-testid={`trend-dim-${d.dimension_id}`}>
                      <span className="text-[12.5px] text-zinc-700 flex-1 truncate">{d.name}</span>
                      <span className="text-[12px] text-zinc-500 tabular-nums w-24 text-right">{d.previous ?? "—"} → {d.current ?? "—"}</span>
                      <span className={`flex items-center gap-1 w-16 justify-end ${cfg.cls}`}>
                        <Icon size={13}/> <span className="text-[12px] font-medium tabular-nums">{deltaLabel(d.delta)}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
